import { useState } from 'react';
import { Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { parseParticipantsList } from '@/lib/parse-participants';
import { addParticipantsBulk, MAX_PARTICIPANTS } from '@/data/participants';
import type { Discipline } from '@/data/types';

interface Props {
  tournamentId: string;
  discipline: Discipline;
  currentCount: number;
}

export function BulkImportDialog({ tournamentId, discipline, currentCount }: Props) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const placeholder = discipline === 'SINGLES'
    ? 'Иванов Иван\nПетров Пётр, Спартак\nСидоров'
    : 'Иванов / Петров\nСидоров, Спартак / Кузнецов, Динамо';

  const parsed = parseParticipantsList(text, discipline);
  const remaining = MAX_PARTICIPANTS - currentCount;
  const overflow = parsed.entries.length > remaining;

  async function handleImport() {
    if (parsed.errors.length > 0) {
      toast.error('Есть ошибки в списке', {
        description: parsed.errors.map((e) => `строка ${e.line}: ${e.message}`).join('\n'),
      });
      return;
    }
    if (parsed.entries.length === 0) {
      toast.error('Список пуст');
      return;
    }
    if (overflow) {
      toast.error(`Перебор: лимит ${MAX_PARTICIPANTS}, осталось мест ${remaining}`);
      return;
    }

    setSubmitting(true);
    try {
      await addParticipantsBulk(tournamentId, parsed.entries);
      toast.success(`Добавлено: ${parsed.entries.length}`);
      setText('');
      setOpen(false);
    } catch (err) {
      toast.error('Не получилось', { description: String((err as Error).message) });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn">
          <Upload size={14} /> Импорт списком
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Импорт участников</DialogTitle>
          <DialogDescription>
            {discipline === 'SINGLES'
              ? 'Один участник в строке. Опционально через запятую — клуб.'
              : 'Одна пара в строке. Имена разделять "/". После имени через запятую — клуб.'}
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: 'grid', gap: 6 }}>
          <label className="label">Список</label>
          <textarea
            className="textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            style={{ minHeight: 200, fontFamily: 'var(--font-mono)', fontSize: 13 }}
          />
        </div>

        <div className="t-small" style={{ display: 'grid', gap: 4 }}>
          <p>
            Распознано: <strong>{parsed.entries.length}</strong> · Свободных мест: {remaining}
          </p>
          {parsed.errors.length > 0 && (
            <ul style={{ color: 'var(--red)', display: 'grid', gap: 2 }}>
              {parsed.errors.map((e, i) => (
                <li key={i}>
                  Строка {e.line}: {e.message}
                </li>
              ))}
            </ul>
          )}
          {overflow && (
            <p style={{ color: 'var(--red)' }}>
              Перебор лимита — оставьте не больше {remaining}.
            </p>
          )}
        </div>

        <DialogFooter>
          <button className="btn btn-ghost" onClick={() => setOpen(false)}>
            Отмена
          </button>
          <button
            className="btn btn-primary"
            onClick={handleImport}
            disabled={submitting || parsed.errors.length > 0 || parsed.entries.length === 0 || overflow}
          >
            {submitting && <span className="spinner" />}
            {submitting ? 'Импорт...' : `Импортировать ${parsed.entries.length}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
